<h2 mat-dialog-title class="!text-base !font-bold !text-slate-900 flex items-center gap-2">
  <mat-icon class="text-blue-600 text-lg">edit</mat-icon>
  {{ 'hotline.edit.title' | translate }}
</h2>

<mat-dialog-content class="!pt-2">
  <div class="flex flex-col gap-4">

    <!-- Name -->
    <div>
      <label class="text-xs font-medium text-slate-500 mb-1 block" for="supplierName">
        {{ 'hotline.edit.name' | translate }}
      </label>
      <input id="supplierName" type="text" [ngModel]="name()" (ngModelChange)="name.set($event)"
        class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
    </div>

    <!--
      The person this right belongs to. Only rendered once the parker has actually loaded — a right
      bought through the quick supplier form is owned by a no-account parker, which this endpoint does
      not return, and showing empty name boxes that silently save nothing would be worse than
      showing none.
    -->
    @if (canEditParker && parkerLoaded()) {
    <div class="border-t border-slate-100 pt-3">
      <label class="text-xs font-medium text-slate-500 mb-1 block">
        {{ 'hotline.edit.memberSection' | translate }}
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input type="text" [ngModel]="parkerFirstName()" (ngModelChange)="parkerFirstName.set($event)"
          [placeholder]="'hotline.edit.firstName' | translate"
          class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <input type="text" [ngModel]="parkerLastName()" (ngModelChange)="parkerLastName.set($event)"
          [placeholder]="'hotline.edit.lastName' | translate"
          class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <input type="email" [ngModel]="parkerEmail()" (ngModelChange)="parkerEmail.set($event)"
        [placeholder]="'hotline.edit.email' | translate"
        class="w-full mt-2 border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      <p class="mt-1 text-[11px] text-slate-400">{{ 'hotline.edit.emailHint' | translate }}</p>
    </div>
    }

    <!-- License plates -->
    <div>
      <div class="flex items-center justify-between mb-1">
        <label class="text-xs font-medium text-slate-500">{{ 'hotline.edit.plates' | translate }}</label>
        <button type="button" (click)="addPlate()"
          class="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 transition">
          <mat-icon class="text-sm w-4 h-4 leading-none">add</mat-icon>
          {{ 'hotline.edit.addPlate' | translate }}
        </button>
      </div>

      <div class="flex flex-col gap-2">
        @for (row of plateRows(); track $index) {
        <div class="flex items-center gap-2">
          <input type="text" [ngModel]="row.fullNumber" (ngModelChange)="onPlateChange($index, $event)"
            [placeholder]="'hotline.edit.platePlaceholder' | translate"
            class="flex-1 border border-slate-200 rounded-lg py-1.5 px-2 text-sm font-mono uppercase bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button type="button" (click)="removePlate($index)" [disabled]="plateRows().length === 1"
            [title]="'hotline.edit.removePlate' | translate"
            class="shrink-0 text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition flex items-center">
            <mat-icon class="text-lg">delete_outline</mat-icon>
          </button>
        </div>
        }
      </div>
      <p class="text-xs text-slate-400 mt-1">{{ 'hotline.edit.platesHelp' | translate }}</p>
    </div>

    <!-- Comment (carries the AK number / contact person) -->
    <div>
      <label class="text-xs font-medium text-slate-500 mb-1 block" for="supplierComment">
        {{ 'hotline.edit.comment' | translate }}
      </label>
      <textarea id="supplierComment" rows="3" [ngModel]="comment()" (ngModelChange)="comment.set($event)"
        [placeholder]="'hotline.edit.commentPlaceholder' | translate"
        class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
      <p class="text-xs text-slate-400 mt-1">{{ 'hotline.edit.commentHelp' | translate }}</p>
    </div>

    <!-- Valid from -->
    <div>
      <label class="text-xs font-medium text-slate-500 mb-1 block" for="supplierValidFrom">
        {{ 'hotline.longTerm.validFrom' | translate }}
      </label>
      <app-date-field inputId="supplierValidFrom" [(value)]="validFrom" />
    </div>

    <!-- Valid until -->
    <div>
      <label class="text-xs font-medium text-slate-500 mb-1 block" for="supplierValidTo">
        {{ 'hotline.edit.validTo' | translate }}
      </label>
      <app-date-field inputId="supplierValidTo" [(value)]="validTo" />
      <p class="text-xs text-slate-400 mt-1">{{ 'hotline.edit.validToHelp' | translate }}</p>
    </div>

    <!-- Zones -->
    @if (data.availableZones.length) {
    <div>
      <label class="text-xs font-medium text-slate-500 mb-1 block">{{ 'hotline.edit.zones' | translate }}</label>
      <div class="flex flex-wrap gap-1.5">
        @for (zone of data.availableZones; track zone.id) {
        <button type="button" (click)="toggleZone(zone.id)"
          class="px-2.5 py-1 text-xs font-semibold rounded-full border transition"
          [class]="isZoneSelected(zone.id)
            ? 'bg-blue-600 border-blue-600 text-white'
            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'">
          {{ zone.name || ('hotline.unnamedZone' | translate) }}
        </button>
        }
      </div>
      <p class="text-xs text-slate-400 mt-1">{{ 'hotline.edit.zonesHelp' | translate }}</p>
    </div>
    }

    <!-- Company contact. Stored on the Organization, so only offered when the right is linked to
         one — the quick single-supplier form never sets that link. -->
    @if (canEditContact) {
    <div>
      <label class="text-xs font-medium text-slate-500 mb-1 block">
        {{ 'hotline.edit.contact' | translate }}
      </label>
      @if (isLoadingContact()) {
      <div class="flex items-center gap-2 text-xs text-slate-400 py-2">
        <mat-spinner diameter="16"></mat-spinner>
        <span>{{ 'hotline.edit.contactLoading' | translate }}</span>
      </div>
      } @else {
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input type="text" [ngModel]="contactPerson()" (ngModelChange)="contactPerson.set($event)"
          [placeholder]="'hotline.edit.contactPerson' | translate"
          class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-blue-500 focus:outline-none" />
        <input type="tel" [ngModel]="contactPhone()" (ngModelChange)="contactPhone.set($event)"
          [placeholder]="'hotline.edit.contactPhone' | translate"
          class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-blue-500 focus:outline-none" />
      </div>
      }
    </div>
    }

  </div>
</mat-dialog-content>

<mat-dialog-actions class="!px-6 !pb-4 flex justify-end gap-2">
  <button type="button" (click)="cancel()" [disabled]="isSaving()"
    class="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition">
    {{ 'common.cancel' | translate }}
  </button>
  <button type="button" (click)="save()" [disabled]="isSaving() || !canSave()"
    class="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition inline-flex items-center gap-2">
    @if (isSaving()) {
    <mat-spinner diameter="16"></mat-spinner>
    }
    {{ 'common.save' | translate }}
  </button>
</mat-dialog-actions>
